import {
  getToken as _getToken,
  onMessage as _onMessage,
  deleteToken as _deleteToken,
} from 'firebase/messaging'
import type { MessagePayload, Messaging } from 'firebase/messaging'
import type { FcmNotification } from '@@/shared-components/types/Notification'
import type { RouteLocationRaw } from '#vue-router'

type Notifications = {
  notificationToken?: string
}
declare global {
  interface Window extends Notifications {}
}

const FCM_SCOPE = '/fcm'

export type PUSH_TOKEN_TYPE = 'inventory' | 'profile' | 'tag' | 'device' | 'all'

export const registerPushToken = async (
  token: string,
  type: PUSH_TOKEN_TYPE,
  object?: number | string
) => {
  const { post } = useApi()
  const { browser, os } = useGetBrowser()

  return post('/push-tokens', {
    token,
    type,
    object,
    name: 'push-token',
    device: {
      os,
      browser,
      type: 'admin-client',
    },
  })
}

export const unRegisterPushToken = async (
  token: string,
  type: PUSH_TOKEN_TYPE,
  object?: number | string
) => {
  const { remove } = useApi()
  return remove(`/push-tokens/${token}`, {
    type,
    object,
  })
}

const FCM_TOKEN_COOKIE_KEY = 'fcmToken'
const getServiceWorkerPath = () => {
  return `${window.location.origin}/firebase-messaging-sw${import.meta.dev && !import.meta.env.PROD ? '.dev' : ''}.js`
}

export const useFcm = (fcmProps?: { messaging: Messaging }) => {
  const nuxtApp = useNuxtApp()
  const fcm = fcmProps ?? (nuxtApp.$fcm as { messaging: Messaging })
  const fcmToken = useState<string | undefined>(
    'fcmToken',
    () => useCookie(FCM_TOKEN_COOKIE_KEY, { sameSite: true, secure: true })?.value || undefined
  )
  const callbacks = useState<{ [pageKey: string]: ((payload: FcmNotification) => void)[] }>(
    'fcmCallbacks',
    () => ({})
  )
  const instance = getCurrentInstance()
  const pageId = instance ? (useId() ?? 'global') : 'global'

  if (instance) {
    onBeforeUnmount(() => {
      if (callbacks.value[pageId]) {
        delete callbacks.value[pageId]
      }
    })
  }

  const { addInfoMessage } = useToastService()
  const { playMusic } = useMusic()

  const notify = (
    title: string,
    body?: string,
    link?: RouteLocationRaw,
    type?: string,
    withSound?: boolean
  ) => {
    addInfoMessage(title, body, 3600000, link)
    if (withSound) {
      playMusic(type)
    }
  }

  const isPageConnectedToFcm = computed(
    () => Object.keys(callbacks.value).filter((k) => k !== 'global').length
  )

  const getToken = async (): Promise<string | undefined> => {
    if (import.meta.client) {
      const cookieToken = useCookie(FCM_TOKEN_COOKIE_KEY, { sameSite: true, secure: true })
      const serviceWorkerPath = getServiceWorkerPath()
      if (fcm?.messaging) {
        const serviceWorkerRegistration =
          'serviceWorker' in navigator
            ? await navigator.serviceWorker.register(serviceWorkerPath, { scope: FCM_SCOPE })
            : undefined
        // Čekání na aktivaci serviceWorkerRegistration.active
        if (serviceWorkerRegistration) {
          // Clean-up mechanism for timeouts and navigation away
          const timeouts: NodeJS.Timeout[] = []
          let cancelled = false
          const clean = () => {
            cancelled = true
            timeouts.forEach((t) => clearTimeout(t))
          }
          // Clean-up on page leave (unmount or navigation)
          const beforeUnloadHandler = () => clean()
          window.addEventListener('beforeunload', beforeUnloadHandler)
          if (instance) {
            onBeforeUnmount(() => {
              clean()
              window.removeEventListener('beforeunload', beforeUnloadHandler)
            })
          }
          try {
            await (async () => {
              const delays = [1000, 3000, 5000]
              for (let i = 0; i < delays.length; i++) {
                if (serviceWorkerRegistration.active || cancelled) return
                await new Promise((resolve) => {
                  const timeout = setTimeout(resolve, delays[i])
                  timeouts.push(timeout)
                })
                if (serviceWorkerRegistration.active || cancelled) return
              }
            })()
          } finally {
            clean()
            window.removeEventListener('beforeunload', beforeUnloadHandler)
          }
        }
        switch (Notification.permission) {
          case 'default': {
            try {
              const token = await _getToken(
                fcm.messaging,
                serviceWorkerRegistration ? { serviceWorkerRegistration } : undefined
              )
              fcmToken.value = token
              cookieToken.value = token
            } catch (e) {
              if (!e.message?.includes('messaging/permission-blocked')) {
                console.debug('FCM token error:', e)
              }
            }
            break
          }
          case 'granted': {
            try {
              const token = await _getToken(
                fcm.messaging,
                serviceWorkerRegistration ? { serviceWorkerRegistration } : undefined
              )
              fcmToken.value = token
              cookieToken.value = token
            } catch (e: any) {
              console.debug('FCM token error:', e)
            }
            break
          }
        }
      } else if (window.kioskMode && !window.notificationToken) {
        window.postMessage('get-fcm-token')
        const token = await new Promise<string | null>((resolve) => {
          document.addEventListener(
            'set-notification-token',
            (event: Event) => {
              const token = (event as CustomEvent).detail as string | null
              resolve(token)
            },
            { once: true }
          )
        })
        const value = token ?? undefined
        fcmToken.value = value
        cookieToken.value = value
      }
    }
    return fcmToken.value
  }

  const deleteToken = async (token: string, type: PUSH_TOKEN_TYPE, object?: number | string) => {
    const cookieToken = useCookie(FCM_TOKEN_COOKIE_KEY, { sameSite: true, secure: true })
    if (fcm?.messaging) {
      // HOTIFX?
      // https://github.com/firebase/firebase-js-sdk/issues/9384
      let activeServiceWoker = false
      await navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          const serviceWorkerPath = getServiceWorkerPath()
          if (registration.active?.scriptURL === serviceWorkerPath) {
            activeServiceWoker = true
          }
        }
      })
      if (activeServiceWoker) {
        const isTokenDeleted = await _deleteToken(fcm?.messaging)
        if (!isTokenDeleted) {
          throw new Error('Nastala chyba při odregistraci FCM tokenu u Firebase')
        }
      }
      const res = await unRegisterPushToken(token, type, object)
      fcmToken.value = undefined
      cookieToken.value = undefined
      return res
    } else if (window.kioskMode && !window.notificationToken) {
      window.postMessage('delete-fcm-token')
      await new Promise((resolve) => {
        document.addEventListener(
          'fcm-token-deleted',
          () => {
            resolve(true)
          },
          { once: true }
        )
      })
      const res = await unRegisterPushToken(token, type, object)
      fcmToken.value = undefined
      cookieToken.value = undefined
      return res
    }
  }

  const callCallbacks = (payload: MessagePayload, parseJSON = true) => {
    const parseJSONStrings = (obj: MessagePayload) => {
      // Procházej všechny klíče objektu
      for (const key in obj) {
        if (typeof obj[key] === 'string') {
          try {
            // Pokus se převést hodnotu na JSON
            const parsedValue = JSON.parse(obj[key])

            // Pokud se podařilo převést, ulož hodnotu zpět (rekurzivní kontrola)
            obj[key] = parseJSONStrings(parsedValue)
          } catch (e) {
            // Pokud není validní JSON, ponech hodnotu jako je
            // Chyba je očekávaná, nemusíme s ní nic dělat
          }
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          // Pokud je hodnota objekt nebo pole, procházej rekurzivně
          parseJSONStrings(obj[key])
        }
      }
      return obj
    }
    const data = (parseJSON ? parseJSONStrings(payload) : payload) as FcmNotification
    try {
      if (callbacks.value[pageId]) {
        callbacks.value[pageId].forEach((callback) => {
          callback(data)
        })
      }
      if (callbacks.value.global) {
        callbacks.value.global.forEach((callback) => {
          callback(data)
        })
      }
    } catch (e) {
      console.error(e)
    }
  }

  const onDeviceNotification = (e: Event) => {
    const payload = (<CustomEvent>e).detail as MessagePayload | string
    // Je zde dočasně z důvodu zpětné kompatibility se staršími verzemi kiosk aplikace
    if (typeof payload === 'string') {
      callCallbacks(JSON.parse(payload), false)
    } else {
      callCallbacks(payload)
    }
  }

  const onMessage = (cb: (payload: FcmNotification) => void) => {
    if (instance) {
      onMounted(() => {
        document.addEventListener('device-notification', onDeviceNotification)
      })
      onBeforeUnmount(() => {
        document.removeEventListener('device-notification', onDeviceNotification)
      })
    }
    if (callbacks.value[pageId]) {
      callbacks.value = {
        ...callbacks.value,
        [pageId]: [...callbacks.value[pageId], cb],
      }
    } else {
      callbacks.value = {
        ...callbacks.value,
        [pageId]: [cb],
      }
    }
  }

  const unRegisterServiceWorker = () => {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        if (registration.scope.includes(FCM_SCOPE)) {
          registration.unregister()
        }
      }
    })
  }

  const channel = new BroadcastChannel('notification_broadcast_channel')

  channel.onmessage = (event) => {
    callCallbacks(event.data)
  }

  if (fcm?.messaging) {
    _onMessage(fcm.messaging, (payload) => {
      callCallbacks(payload)
    })
  }

  return {
    isPageConnectedToFcm,
    fcmToken,
    getToken,
    deleteToken,
    notify,
    onMessage,
    unRegisterServiceWorker,
  }
}
